import { useState, useEffect } from 'react';
import { api } from '../api';
import { useTripBuilder } from '../TripBuilderContext';

// Human-readable program labels
const PROGRAM_LABELS = {
  amex_mr: 'Amex MR',
  chase_ur: 'Chase UR',
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function TransferBonusAlert({ balances }) {
  const { amexTotal, chaseTotal } = useTripBuilder();
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifyEnabled, setNotifyEnabled] = useState(() => {
    try {
      return localStorage.getItem('pp_bonus_notify') === '1';
    } catch {
      return false;
    }
  });
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    api.getTransferBonuses()
      .then(data => setBonuses(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function toggleNotify() {
    const next = !notifyEnabled;
    setNotifyEnabled(next);
    try {
      localStorage.setItem('pp_bonus_notify', next ? '1' : '0');
    } catch {
      // localStorage unavailable
    }
  }

  function getBalance(sourceProgram) {
    if (balances) {
      const b = balances.find(b => b.program === sourceProgram);
      if (b) return b.balance;
    }
    if (sourceProgram === 'amex_mr') return amexTotal;
    if (sourceProgram === 'chase_ur') return chaseTotal;
    return 0;
  }

  const activeBonuses = bonuses.filter(b => b.is_active);

  if (loading) {
    return (
      <div className="bg-navy-light border border-brand-ink/10 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-xs text-brand-ink/65">Checking for transfer bonuses...</span>
        </div>
      </div>
    );
  }

  // No active bonuses
  if (activeBonuses.length === 0) {
    return (
      <div className="bg-navy-light border border-brand-ink/10 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-2.5 h-2.5 rounded-full bg-brand-ink/20" />
            <span className="text-sm text-brand-ink/70">No active transfer bonuses</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-brand-ink/55">Check back regularly - bonuses pop up often</span>
            <button
              onClick={toggleNotify}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                notifyEnabled
                  ? 'bg-teal/20 text-teal border border-teal/30'
                  : 'bg-brand-ink/5 text-brand-ink/65 border border-brand-ink/10 hover:bg-brand-ink/10'
              }`}
            >
              <span>{notifyEnabled ? 'ON' : 'OFF'}</span>
              <span className="hidden sm:inline">Notify me</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active bonuses exist
  return (
    <div className="space-y-3">
      {activeBonuses.map(bonus => {
        const sourceLabel = PROGRAM_LABELS[bonus.source_program] || bonus.source_program;
        const targetLabel = bonus.airline_program_short || bonus.airline_program;
        const pct = bonus.bonus_percentage;
        const balance = getBalance(bonus.source_program);
        const exampleTransfer = 100000;
        const exampleReceive = Math.round(exampleTransfer * (1 + pct / 100));
        const fullBalance = balance;
        const fullReceive = Math.round(fullBalance * (1 + pct / 100));
        const endDate = bonus.end_date ? formatDate(bonus.end_date) : null;
        const isExpanded = expandedId === bonus.id;

        return (
          <div
            key={bonus.id}
            className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl overflow-hidden"
          >
            {/* Main banner */}
            <button
              onClick={() => setExpandedId(isExpanded ? null : bonus.id)}
              className="w-full text-left p-4 group"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-lg leading-none">&#128293;</span>
                    <span className="text-sm font-bold text-amber-400 uppercase tracking-wide">
                      Active Bonus: {pct}% bonus
                    </span>
                    {endDate && (
                      <span className="text-[10px] text-brand-ink/65 bg-brand-ink/5 px-2 py-0.5 rounded-full">
                        expires {endDate}
                      </span>
                    )}
                  </div>
                  <div className="text-white font-semibold">
                    {pct}% bonus transferring {sourceLabel} &rarr; {targetLabel}
                  </div>
                  <div className="text-sm text-brand-ink/75 mt-1">
                    Transfer {(exampleTransfer).toLocaleString()} &rarr; get {exampleReceive.toLocaleString()}.{' '}
                    <span className="text-amber-300 font-medium">
                      Your {fullBalance.toLocaleString()} {sourceLabel} becomes {fullReceive.toLocaleString()} {targetLabel}!
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 mt-1">
                  <span className={`text-brand-ink/55 text-xs transition-transform inline-block ${isExpanded ? 'rotate-180' : ''}`}>
                    &#9660;
                  </span>
                </div>
              </div>
            </button>

            {/* Expanded detail */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-amber-500/20 pt-3 space-y-3 animate-fade-in">
                {/* Transfer math table */}
                <div className="bg-black/20 rounded-lg p-3">
                  <div className="text-[10px] font-bold text-brand-ink/65 uppercase tracking-wider mb-2">Transfer Calculator</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="text-brand-ink/65">You transfer</div>
                    <div className="text-brand-ink/65">Bonus ({pct}%)</div>
                    <div className="text-brand-ink/65">You receive</div>

                    {[50000, 100000, 200000, fullBalance].filter((v, i, a) => a.indexOf(v) === i && v > 0).map(amount => {
                      const bonusAmt = Math.round(amount * pct / 100);
                      const total = amount + bonusAmt;
                      const isFullBalance = amount === fullBalance;
                      return (
                        <div key={amount} className="contents">
                          <div className={`font-medium ${isFullBalance ? 'text-amber-300' : 'text-brand-ink/80'}`}>
                            {amount.toLocaleString()}
                          </div>
                          <div className="text-teal font-medium">
                            +{bonusAmt.toLocaleString()}
                          </div>
                          <div className={`font-bold ${isFullBalance ? 'text-amber-300' : 'text-white'}`}>
                            {total.toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Description */}
                {bonus.description && (
                  <div className="text-xs text-brand-ink/70">
                    {bonus.description}
                  </div>
                )}

                {/* Reminder */}
                <div className="flex items-start gap-2 bg-gold/10 border border-gold/20 rounded-lg p-2.5">
                  <span className="text-gold text-sm leading-none mt-0.5">&#9888;</span>
                  <span className="text-[11px] text-brand-ink/75">
                    <strong className="text-brand-ink/85">Remember:</strong> Never transfer until you have confirmed award availability on the airline/hotel website. Transfers are irreversible.
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Notify toggle */}
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={toggleNotify}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
            notifyEnabled
              ? 'bg-teal/20 text-teal border border-teal/30'
              : 'bg-brand-ink/5 text-brand-ink/65 border border-brand-ink/10 hover:bg-brand-ink/10'
          }`}
        >
          <span className="text-sm leading-none">{notifyEnabled ? '\u2705' : '\u{1F514}'}</span>
          <span>{notifyEnabled ? 'Notifications on' : 'Notify me of new bonuses'}</span>
        </button>
      </div>
    </div>
  );
}
